import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import SaveIcon from '@mui/icons-material/Save';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { CustomerAddress } from '../types';
import { accountingApi } from '../../../../config/apiClient';

const TURKEY_CITIES = [
  'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Aksaray', 'Amasya', 'Ankara', 'Antalya',
  'Ardahan', 'Artvin', 'Aydın', 'Balıkesir', 'Bartın', 'Batman', 'Bayburt', 'Bilecik',
  'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa', 'Çanakkale', 'Çankırı', 'Çorum',
  'Denizli', 'Diyarbakır', 'Düzce', 'Edirne', 'Elazığ', 'Erzincan', 'Erzurum', 'Eskişehir',
  'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkari', 'Hatay', 'Iğdır', 'Isparta', 'İstanbul',
  'İzmir', 'Kahramanmaraş', 'Karabük', 'Karaman', 'Kars', 'Kastamonu', 'Kayseri', 'Kırıkkale',
  'Kırklareli', 'Kırşehir', 'Kilis', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa',
  'Mardin', 'Mersin', 'Muğla', 'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Osmaniye',
  'Rize', 'Sakarya', 'Samsun', 'Şanlıurfa', 'Siirt', 'Sinop', 'Şırnak', 'Sivas',
  'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Uşak', 'Van', 'Yalova', 'Yozgat', 'Zonguldak',
];

interface AddressDetailModalProps {
  open: boolean;
  onClose: () => void;
  address: CustomerAddress | null;
  customerName?: string;
  customerPhone?: string;
  accountId?: number;
  onAddressUpdated?: (updatedAddress: CustomerAddress) => void;
  /**
   * Müşterinin temel adı (Account.CompanyName) güncellendiğinde parent'a bildirir.
   * Sadece ad değişikliği başarılı olursa çağrılır. Telefon güncelleme YOK (kasıtlı).
   */
  onCustomerNameUpdated?: (newName: string) => void;
  /** Adres silindiğinde (yalnız hiç siparişte kullanılmamış adres silinebilir). */
  onAddressDeleted?: (addressId: string | number) => void;
}

const AddressDetailModal: React.FC<AddressDetailModalProps> = ({
  open,
  onClose,
  address,
  customerName,
  customerPhone,
  accountId,
  onAddressUpdated,
  onCustomerNameUpdated,
  onAddressDeleted,
}) => {
  const { colors: themeColors } = useThemeContext();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // ✅ Yeni: müşteri adı düzenleme. Telefon EDIT YOK (kasıtlı — kullanıcı isteği).
  const [customerNameInput, setCustomerNameInput] = useState('');

  const [label, setLabel] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [mahalle, setMahalle] = useState('');
  const [cadde, setCadde] = useState('');
  const [sokak, setSokak] = useState('');
  const [apartman, setApartman] = useState('');
  const [siteAdi, setSiteAdi] = useState('');
  const [blok, setBlok] = useState('');
  const [binaNo, setBinaNo] = useState('');
  const [kat, setKat] = useState('');
  const [daireNo, setDaireNo] = useState('');
  const [openAddress, setOpenAddress] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [authorizedPerson, setAuthorizedPerson] = useState('');
  const [phone, setPhone] = useState('');

  // Silme: adrese hiç sipariş gönderilmediyse silinebilir (sunucu da ayrıca kontrol eder)
  const [usage, setUsage] = useState<{ orderCount: number; canDelete: boolean } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setUsage(null);
    setConfirmDelete(false);
    if (!open || !accountId || !address?.id || !onAddressDeleted) return;
    let cancelled = false;
    accountingApi
      .get(`/api/Account/${accountId}/shipping-addresses/${address.id}/usage`)
      .then((res: any) => {
        const data = res?.data?.data ?? res?.data;
        if (!cancelled && data) setUsage({ orderCount: Number(data.orderCount) || 0, canDelete: !!data.canDelete });
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, accountId, address?.id, onAddressDeleted]);

  const handleDelete = async () => {
    if (!accountId || !address) return;
    setIsDeleting(true);
    setError(null);
    try {
      await accountingApi.post(`/api/Account/${accountId}/shipping-addresses/${address.id}/delete`);
      setConfirmDelete(false);
      onAddressDeleted?.(address.id);
      onClose();
    } catch (err: any) {
      setConfirmDelete(false);
      setError(err?.response?.data?.message || 'Adres silinemedi.');
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (address && open) {
      setCustomerNameInput(customerName || '');
      setLabel(address.label || '');
      // '-' backend'in [Required] alanları için gönderilen placeholder; formda boş göster
      setCity(address.city === '-' ? '' : address.city || '');
      setDistrict(address.district === '-' ? '' : address.district || '');
      setMahalle(address.mahalle || '');
      setCadde(address.cadde || '');
      setSokak(address.sokak || '');
      setApartman(address.apartman || '');
      setSiteAdi(address.siteAdi || '');
      setBlok(address.blok || '');
      setBinaNo(address.binaNo || '');
      setKat(address.kat || '');
      setDaireNo(address.daireNo || '');
      setOpenAddress(address.address || '');
      setDeliveryNotes(address.deliveryNotes || '');
      setAuthorizedPerson(address.authorizedPerson || '');
      setPhone(address.phone || '');
      setIsEditMode(false);
      setError(null);
      setSuccess(false);
    }
  }, [address, open, customerName]);

  const handleSave = async () => {
    if (!accountId || !address) return;

    // ✅ Ad zorunlu — müşteri kartının adı boş bırakılamaz
    const trimmedName = customerNameInput.trim();
    if (!trimmedName) {
      setError('Müşteri adı boş olamaz');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // ============================================================
      // 1) Müşteri adı değişmişse önce ONU güncelle (hafif endpoint)
      // Telefon ASLA dokunulmuyor (kullanıcı talebi).
      // ============================================================
      const nameChanged = trimmedName !== (customerName || '').trim();
      if (nameChanged) {
        await accountingApi.post(`/api/Account/${accountId}/update-name`, {
          CompanyName: trimmedName,
        });
      }

      // ============================================================
      // 2) Adres alanlarını güncelle (mevcut endpoint)
      // ============================================================
      // Backend body'deki CompanyId'yi okumuyor (X-Company-Id header'ından alıyor),
      // ancak model [Required] olduğu için boş string 400 döndürüyor -> '_' placeholder.
      // City/District/AuthorizedPerson/Address da [Required]; boş kalırlarsa
      // [ApiController] otomatik 400 verir, bu yüzden '-' fallback zorunlu.
      const updateData = {
        Id: address.id,
        AccountId: accountId,
        CompanyId: '_',
        AddressLabel: label,
        City: city || '-',
        District: district || '-',
        Country: 'Türkiye',
        Address: openAddress || `${mahalle} ${cadde} ${sokak}`.trim() || '-',
        AuthorizedPerson: authorizedPerson || trimmedName || '-',
        Phone: phone || customerPhone || '',
        IsDefault: address.isDefault || false,
        DeliveryNotes: deliveryNotes || undefined,
        Mahalle: mahalle || undefined,
        Cadde: cadde || undefined,
        Sokak: sokak || undefined,
        Apartman: apartman || undefined,
        SiteAdi: siteAdi || undefined,
        Blok: blok || undefined,
        BinaNo: binaNo || undefined,
        Kat: kat || undefined,
        DaireNo: daireNo || undefined,
      };

      await accountingApi.post(
        `/api/Account/${accountId}/shipping-addresses/${address.id}/update`,
        updateData
      );

      setSuccess(true);
      setIsEditMode(false);

      // Parent state senkronizasyonu — ad değiştiyse onCustomerNameUpdated çağır
      if (nameChanged && onCustomerNameUpdated) {
        onCustomerNameUpdated(trimmedName);
      }

      if (onAddressUpdated) {
        onAddressUpdated({
          ...address,
          label,
          city,
          district,
          address: openAddress,
          mahalle,
          cadde,
          sokak,
          apartman,
          siteAdi,
          blok,
          binaNo,
          kat,
          daireNo,
          deliveryNotes,
          authorizedPerson,
          phone,
        });
      }

      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('[AddressDetail] Güncelleme hatası:', err);
      // ASP.NET model validasyonu 400'lerinde mesaj `errors` sözlüğünde gelir
      const validationErrors = err?.response?.data?.errors;
      const validationMessage = validationErrors
        ? Object.values(validationErrors).flat().join(' ')
        : null;
      setError(err?.response?.data?.message || validationMessage || 'Güncellenirken bir hata oluştu.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!address) return null;

  const InfoRow = ({ label: rowLabel, value }: { label: string; value?: string }) => {
    if (!value) return null;
    return (
      <Box display="flex" gap={1} py={0.3}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, fontWeight: 600 }}>
          {rowLabel}:
        </Typography>
        <Typography variant="caption">{value}</Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          bgcolor: themeColors.primary,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1.5,
        }}
      >
        <LocationOnIcon />
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
          Adres Detayı
        </Typography>
        {address.isDefault && (
          <Chip label="Varsayılan" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.7rem' }} />
        )}
        {!isEditMode && accountId && (
          <IconButton size="small" onClick={() => setIsEditMode(true)} sx={{ color: 'white' }}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.75rem' }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 1.5, fontSize: '0.75rem' }}>Adres başarıyla güncellendi.</Alert>}

        {/* Müşteri Bilgileri — Ad düzenlenebilir, Telefon her zaman read-only */}
        {(customerName || customerPhone || accountId) && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Box display="flex" alignItems="center" gap={0.5} mb={isEditMode ? 1 : 0.5}>
              <PersonIcon sx={{ fontSize: 16, color: themeColors.primary }} />
              <Typography variant="caption" fontWeight="bold" color={themeColors.primary}>
                Müşteri Bilgileri
              </Typography>
            </Box>

            {isEditMode && accountId ? (
              <Box display="flex" flexDirection="column" gap={1}>
                <TextField
                  label="Ad Soyad"
                  value={customerNameInput}
                  onChange={(e) => setCustomerNameInput(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={!customerNameInput.trim()}
                  helperText={!customerNameInput.trim() ? 'Müşteri adı zorunludur' : ' '}
                  inputProps={{ maxLength: 100 }}
                />
                {customerPhone && (
                  <InfoRow label="Telefon" value={customerPhone} />
                )}
              </Box>
            ) : (
              <>
                {(customerName || customerNameInput) && (
                  <InfoRow label="Ad Soyad" value={customerNameInput || customerName} />
                )}
                {customerPhone && <InfoRow label="Telefon" value={customerPhone} />}
              </>
            )}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {!isEditMode ? (
          /* Görüntüleme Modu */
          <Box>
            <Box display="flex" alignItems="center" gap={0.5} mb={1}>
              <LocationOnIcon sx={{ fontSize: 16, color: themeColors.primary }} />
              <Typography variant="caption" fontWeight="bold" color={themeColors.primary}>
                {label || 'Adres'}
              </Typography>
            </Box>

            <InfoRow label="İl" value={city} />
            <InfoRow label="İlçe" value={district} />
            <InfoRow label="Mahalle" value={mahalle} />
            <InfoRow label="Cadde" value={cadde} />
            <InfoRow label="Sokak" value={sokak} />
            <InfoRow label="Apartman" value={apartman} />
            <InfoRow label="Site Adı" value={siteAdi} />
            <InfoRow label="Blok" value={blok} />
            <InfoRow label="Bina No" value={binaNo} />
            <InfoRow label="Kat" value={kat} />
            <InfoRow label="Daire No" value={daireNo} />
            <InfoRow label="Açık Adres" value={openAddress} />

            {(authorizedPerson || phone) && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <PhoneIcon sx={{ fontSize: 14, color: 'grey.600' }} />
                  <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    Teslimat İletişim
                  </Typography>
                </Box>
                <InfoRow label="Yetkili" value={authorizedPerson} />
                <InfoRow label="Telefon" value={phone} />
              </>
            )}

            {deliveryNotes && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  Teslimat Notu
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, p: 1, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                  {deliveryNotes}
                </Typography>
              </>
            )}
          </Box>
        ) : (
          /* Düzenleme Modu */
          <Box>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField
                  label="Adres Etiketi"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Ev, İş, vb."
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>İl</InputLabel>
                  <Select value={city} onChange={(e) => setCity(e.target.value)} label="İl">
                    <MenuItem value=""><em>Seçilmedi</em></MenuItem>
                    {TURKEY_CITIES.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="İlçe"
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Mahalle"
                  value={mahalle}
                  onChange={(e) => setMahalle(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Cadde"
                  value={cadde}
                  onChange={(e) => setCadde(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Sokak"
                  value={sokak}
                  onChange={(e) => setSokak(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Apartman / Bina Adı"
                  value={apartman}
                  onChange={(e) => setApartman(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Site Adı"
                  value={siteAdi}
                  onChange={(e) => setSiteAdi(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField label="Blok" value={blok} onChange={(e) => setBlok(e.target.value)} fullWidth size="small" />
              </Grid>
              <Grid item xs={3}>
                <TextField label="Bina No" value={binaNo} onChange={(e) => setBinaNo(e.target.value)} fullWidth size="small" />
              </Grid>
              <Grid item xs={3}>
                <TextField label="Kat" value={kat} onChange={(e) => setKat(e.target.value)} fullWidth size="small" />
              </Grid>
              <Grid item xs={3}>
                <TextField label="Daire No" value={daireNo} onChange={(e) => setDaireNo(e.target.value)} fullWidth size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Açık Adres"
                  value={openAddress}
                  onChange={(e) => setOpenAddress(e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Yetkili Kişi"
                  value={authorizedPerson}
                  onChange={(e) => setAuthorizedPerson(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Telefon"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Teslimat Notu"
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5, pt: 1 }}>
        {isEditMode ? (
          <>
            <Button size="small" onClick={() => setIsEditMode(false)} color="inherit">
              Vazgeç
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
              sx={{ bgcolor: themeColors.primary }}
            >
              {isSaving ? 'Kaydediliyor...' : 'Güncelle'}
            </Button>
          </>
        ) : (
          <>
            {accountId && onAddressDeleted && usage && (
              <Tooltip
                title={usage.canDelete ? '' : `Bu adrese daha önce ${usage.orderCount} sipariş gönderilmiş; silinemez. Düzenleyebilirsiniz.`}
              >
                <span style={{ marginRight: 'auto' }}>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    disabled={!usage.canDelete || isDeleting}
                    onClick={() => setConfirmDelete(true)}
                  >
                    Adresi sil
                  </Button>
                </span>
              </Tooltip>
            )}
            {accountId && (
              <Button size="small" variant="outlined" onClick={() => setIsEditMode(true)} startIcon={<EditIcon />}>
                Düzenle
              </Button>
            )}
            <Button size="small" onClick={onClose}>
              Kapat
            </Button>
          </>
        )}
      </DialogActions>

      {/* Silme onayı */}
      <Dialog open={confirmDelete} onClose={isDeleting ? undefined : () => setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteOutlineIcon color="error" />
          Adres silinsin mi?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{address.label || 'Adres'}</strong> adresi müşterinin adres listesinden kaldırılacak.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {[address.mahalle, address.cadde, address.sokak, address.district, address.city].filter((x) => x && x !== '-').join(', ') || address.address}
          </Typography>
          <Alert severity="info" sx={{ py: 0 }}>
            Bu adrese daha önce hiç sipariş gönderilmedi; silmek geçmiş kayıtları etkilemez.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button size="small" color="inherit" disabled={isDeleting} onClick={() => setConfirmDelete(false)}>
            Vazgeç
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            onClick={handleDelete}
          >
            {isDeleting ? 'Siliniyor...' : 'Evet, sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AddressDetailModal;
